import { useEffect, useRef, useState } from "react";
import { DataGrid, GridCol } from "@/components/DataGrid";
import { Panel } from "@/components/Panel";
import { Tile } from "@/components/Tile";
import { DateRange } from "@/components/DateRange";
import { latar } from "@/lib/progress";

const wib = (d?: string | null) => (d ? new Date(new Date(d).getTime() + 7 * 3600e3).toISOString().slice(0, 16).replace("T", " ") : "-");

/** Nama vendor tidak ditampilkan ke pengguna tenant — sumber data dikenal sebagai kanalnya. */
const SUMBER: Record<string, string> = { digivla: "Media Data", neticle: "Social Data" };
const namaSumber = (v: string) => SUMBER[v] ?? v;

/** Teks error datang mentah dari vendor dan tersimpan apa adanya untuk audit;
 *  nama dan jalur endpoint disamarkan hanya di layar. */
const samarkan = (t: string) => t
  .replace(/data\.neticle\.com/gi, "sumber social")
  .replace(/api\.digivla\.id/gi, "sumber media")
  .replace(/user\/editingplus\/?/gi, "endpoint lokasi")
  .replace(/user\/editing\/?/gi, "endpoint artikel")
  .replace(/dashboard\/media-count\/articles/gi, "endpoint artikel realtime")
  .replace(/dashboard\/media-count/gi, "endpoint daftar artikel")
  .replace(/dashboard\/download-excel/gi, "endpoint ekspor")
  .replace(/neticle/gi, "Social Data")
  .replace(/digivla/gi, "Media Data");

const umurTeks = (m: number | null | undefined) =>
  m == null ? "belum pernah" : m < 1 ? "baru saja" : m < 60 ? `${m} menit lalu` : m < 1440 ? `${Math.round(m / 60)} jam lalu` : `${Math.round(m / 1440)} hari lalu`;

interface GapVendor {
  total: number; from: string | null; to: string | null;
  hariDiperiksa: number; hariKosong: string[];
  sync: { lastOkAt: string | null; umurMenit: number | null; lastStatus: string | null };
  cek: { at: string | null; umurMenit: number | null; meta: any } | null;
}
interface BarisLog { at: string; level: "info" | "warn" | "error"; tenant: string | null; msg: string }

export default function SyncPage() {
  const [f, setF] = useState<any>(null);
  const [gap, setGap] = useState<{ vendor: Record<string, GapVendor> } | null>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [msg, setMsg] = useState("");
  const [sibuk, setSibuk] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(new Date(Date.now() - 6 * 86400e3).toISOString().slice(0, 10));
  const [end, setEnd] = useState(today);

  const [logBuka, setLogBuka] = useState(false);
  const [log, setLog] = useState<{ redisOk: boolean; baris: BarisLog[] } | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);
  const [ikutTurun, setIkutTurun] = useState(true);

  async function refresh() {
    // latar(): halaman ini menarik ulang tiap 5 detik. Tanpa penandaan ini
    // indikator muat akan berkedip terus-menerus tanpa ada yang menunggu apa pun.
    const [a, b, c] = await Promise.all(latar(() => [
      fetch("/api/v1/datasources/freshness"),
      fetch("/api/v1/sync/runs"),
      fetch("/api/v1/datasources/gap?days=30"),
    ]));
    if (a.status === 401) { location.href = "/login"; return; }
    setF(await a.json());
    setRuns((await b.json()).data ?? []);
    if (c.ok) setGap(await c.json());
  }
  useEffect(() => { refresh(); const t = setInterval(refresh, 5000); return () => clearInterval(t); }, []);

  // Log hanya ditarik saat panelnya dibuka — tidak ada gunanya membebani API
  // setiap 2 detik untuk sesuatu yang tidak sedang dilihat siapa pun.
  useEffect(() => {
    if (!logBuka) return;
    let hidup = true;
    const tarik = async () => {
      try {
        const r = await latar(() => fetch("/api/v1/sync/log?limit=200"));
        if (!r.ok || !hidup) return;
        setLog(await r.json());
      } catch { /* biarkan tampilan lama */ }
    };
    tarik();
    const it = setInterval(tarik, 2000);
    return () => { hidup = false; clearInterval(it); };
  }, [logBuka]);

  useEffect(() => {
    if (!logBuka || !ikutTurun || !logRef.current) return;
    logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log, logBuka, ikutTurun]);

  async function sync(jenis: "media" | "social") {
    setSibuk(jenis);
    setMsg("");
    try {
      const r = await fetch("/api/v1/sync", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobType: jenis, startDate: start, endDate: end }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "gagal");
      setMsg(`${jenis === "media" ? "Media Data" : "Social Data"} diantrekan (${(j.jobs ?? []).length} job). Buka log worker untuk melihat prosesnya.`);
      setLogBuka(true);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "gagal");
    } finally { setSibuk(null); }
  }

  async function periksa(jenis: "media" | "social") {
    setMsg("");
    const jobType = jenis === "media" ? "parity-media" : "parity";
    const body: any = { jobType };
    if (jenis === "media") { body.startDate = start; body.endDate = end; }
    const r = await fetch("/api/v1/sync", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const j = await r.json();
    setMsg(r.ok ? "Pemeriksaan kelengkapan diantrekan." : j.error ?? "gagal");
  }

  const workerMati = (f?.jobs?.editing?.lastStatus === "error") || (f?.jobs?.social?.lastStatus === "error");

  const KARTU = [
    { key: "media" as const, vendor: "digivla", judul: "Media Data", ikon: "fa-solid fa-newspaper",
      isi: "Artikel media massa beserta lokasi dan tokoh yang dikutip." },
    { key: "social" as const, vendor: "neticle", judul: "Social Data", ikon: "fa-solid fa-bullhorn",
      isi: "Mention media sosial beserta label analitiknya." },
  ];

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Sync Data</h1>
          <div className="avio-t-sm avio-muted">Tarik data terbaru dari sumber. Diproses di latar oleh worker.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <DateRange start={start} end={end} onApply={(s2, e2) => { setStart(s2); setEnd(e2); }} />
        </div>
      </div>

      {f && (
        <div className="avio-tiles">
          <Tile c={1} icon="fa-solid fa-newspaper" label="Media Data" value={f.coverageByVendor?.digivla?.total ?? 0}
            sub={`terakhir ${umurTeks(gap?.vendor?.digivla?.sync?.umurMenit)}`} />
          <Tile c={3} icon="fa-solid fa-bullhorn" label="Social Data" value={f.coverageByVendor?.neticle?.total ?? 0}
            sub={`terakhir ${umurTeks(gap?.vendor?.neticle?.sync?.umurMenit)}`} />
          <Tile c={5} icon="fa-solid fa-clock-rotate-left" label="Run tercatat" value={runs.length} sub="15 terakhir" />
        </div>
      )}

      {msg && <div className="avio-banner" data-tone="info"><i className="fa-solid fa-circle-info avio-banner__icon" /><div>{msg}</div></div>}
      {workerMati && <div className="avio-banner" data-tone="danger"><i className="fa-solid fa-triangle-exclamation avio-banner__icon" /><div>Sinkronisasi terakhir berakhir dengan error — lihat log worker di bawah.</div></div>}

      <div className="avio-grid">
        {KARTU.map((k) => {
          const g = gap?.vendor?.[k.vendor];
          const kosong = g?.hariKosong ?? [];
          const meta = g?.cek?.meta ?? null;
          const hilang = meta?.hilang ?? (Array.isArray(meta?.parity) ? meta.parity.reduce((s: number, p: any) => s + (p.delta ?? 0), 0) : null);
          return (
            <div key={k.key} data-col="6">
              <Panel icon={k.ikon} title={k.judul}>
                <p className="avio-t-sm avio-muted" style={{ margin: "0 0 var(--space-6)" }}>{k.isi}</p>

                <div className="avio-t-sm" style={{ marginBottom: "var(--space-3)" }}>
                  Terakhir tersinkron: <b>{umurTeks(g?.sync?.umurMenit)}</b>
                  <span className="avio-muted"> · {wib(g?.sync?.lastOkAt)}</span>
                </div>
                <div className="avio-t-sm" style={{ marginBottom: "var(--space-3)" }}>
                  Tersimpan: <b>{(g?.total ?? 0).toLocaleString("id-ID")}</b> data
                </div>
                <div className="avio-t-sm" style={{ marginBottom: "var(--space-6)" }}>
                  {kosong.length === 0
                    ? <span style={{ color: "var(--status-success)" }}><i className="fa-solid fa-circle-check" /> Tidak ada hari kosong dalam 30 hari terakhir.</span>
                    : <span style={{ color: "var(--status-warn)" }}><i className="fa-solid fa-triangle-exclamation" /> {kosong.length} hari tanpa data.</span>}
                  <div className="avio-sub" style={{ marginTop: 2 }}>
                    {!g?.cek
                      ? "Belum pernah diperiksa terhadap sumber — selisih belum diketahui, bukan nol."
                      : hilang === 0 ? "Pemeriksaan terakhir: tidak ada yang tertinggal."
                      : `Pemeriksaan terakhir: ${hilang} item belum tersimpan.`}
                  </div>
                </div>

                <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
                  <button className="avio-btn" data-variant="primary" disabled={sibuk === k.key} onClick={() => sync(k.key)}>
                    <i className={`fa-solid ${sibuk === k.key ? "fa-circle-notch fa-spin" : "fa-rotate"}`} /> Sync {k.judul}
                  </button>
                  <button className="avio-btn" data-variant="secondary" data-size="sm" onClick={() => periksa(k.key)}>
                    <i className="fa-solid fa-clipboard-check" /> Periksa kelengkapan
                  </button>
                </div>
              </Panel>
            </div>
          );
        })}
      </div>

      <Panel icon="fa-solid fa-terminal" title="Log worker"
        hint={logBuka ? "diperbarui tiap 2 detik" : "tersembunyi"}
        action={
          <div style={{ display: "flex", gap: "var(--space-4)", alignItems: "center" }}>
            {logBuka && (
              <label className="avio-t-xs avio-muted" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={ikutTurun} onChange={(e) => setIkutTurun(e.target.checked)} />
                ikuti baris terbaru
              </label>
            )}
            <button className="avio-btn" data-variant="secondary" data-size="sm" onClick={() => setLogBuka((v) => !v)}>
              <i className={`fa-solid ${logBuka ? "fa-eye-slash" : "fa-eye"}`} /> {logBuka ? "Sembunyikan" : "Tampilkan log"}
            </button>
          </div>
        }>
        {!logBuka ? (
          <p className="avio-t-sm avio-muted" style={{ margin: 0 }}>
            Menampilkan apa yang sedang ditarik worker saat ini, baris demi baris.
          </p>
        ) : log && !log.redisOk ? (
          <div className="avio-banner" data-tone="danger"><i className="fa-solid fa-circle-exclamation avio-banner__icon" />
            <div>Antrean tidak terjangkau — log worker tidak dapat dibaca.</div></div>
        ) : (
          <div ref={logRef} onScroll={(e) => {
              const el = e.currentTarget;
              // Menggulir ke atas berarti pengguna sedang membaca sesuatu; jangan
              // seret dia kembali ke bawah setiap 2 detik.
              if (el.scrollHeight - el.scrollTop - el.clientHeight > 40) setIkutTurun(false);
            }}
            style={{
              maxHeight: 340, overflowY: "auto", background: "var(--color-surface-sunken, #0d1117)",
              color: "#d7dde5", borderRadius: "var(--radius-sm, 8px)", padding: "10px 12px",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, lineHeight: 1.55,
            }}>
            {(log?.baris ?? []).length === 0
              ? <div style={{ opacity: .6 }}>Belum ada baris. Worker mungkin belum berjalan — jalankan <code>pnpm worker</code>.</div>
              : (log?.baris ?? []).map((b, i) => (
                  <div key={i} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: b.level === "error" ? "#ff9a9a" : b.level === "warn" ? "#ffd28a" : undefined }}>
                    <span style={{ opacity: .5 }}>{wib(b.at).slice(11)}</span>{"  "}{samarkan(b.msg)}
                  </div>
                ))}
          </div>
        )}
      </Panel>

      <Panel icon="fa-solid fa-clock-rotate-left" title="Riwayat run" hint="15 terakhir · auto-refresh" flush>
      <div className="avio-tablewrap" style={{ border: 0, borderRadius: 0 }}>
      <DataGrid
        cols={[
          { id: "mulai", header: "Mulai", width: "max", sortable: true, accessor: (r: any) => r.startedAt, cell: (r: any) => wib(r.startedAt) },
          { id: "vendor", header: "Sumber", width: "max", sortable: true, accessor: (r: any) => namaSumber(r.vendor), cell: (r: any) => <span><i className={r.vendor === "neticle" ? "fa-solid fa-bullhorn" : "fa-solid fa-newspaper"} /> {namaSumber(r.vendor)}</span> },
          { id: "status", header: "Status", width: "max", sortable: true, accessor: (r: any) => r.status, cell: (r: any) => (
            <span className="avio-chip" data-shape="pill" style={{
              background: r.status === "ok" ? "var(--status-success-bg)" : r.status === "error" ? "var(--status-danger-bg)" : "var(--status-warn-bg)",
              color: r.status === "ok" ? "var(--status-success)" : r.status === "error" ? "var(--status-danger)" : "var(--status-warn)" }}>
              {r.status}
            </span>) },
          { id: "window", header: "Rentang", width: "max", cell: (r: any) => r.windowStart ? `${wib(r.windowStart).slice(0, 10)} — ${wib(r.windowEnd).slice(0, 10)}` : "-" },
          { id: "seen", header: "Dilihat", width: "max", sortable: true, align: "right", accessor: (r: any) => r.itemsSeen, cell: (r: any) => r.itemsSeen },
          { id: "ups", header: "Tersimpan", width: "max", sortable: true, align: "right", accessor: (r: any) => r.itemsUpserted, cell: (r: any) => r.itemsUpserted },
          { id: "err", header: "Catatan", width: "fr", cell: (r: any) => <span className="avio-sub" style={{ color: r.error ? "var(--sent-neg)" : undefined }}>
              {r.error ? samarkan(String(r.error)).slice(0, 80)
               : r.meta?.hilang != null ? `sumber ${r.meta.vendorTotal} vs tersimpan ${r.meta.dbTotal} (hilang ${r.meta.hilang})`
               : ""}
            </span> },
        ] as GridCol<any>[]}
        data={runs}
        minTableWidth={820}
        emptyText="Belum ada run tercatat."
      />
      </div>
      </Panel>
    </div>
  );
}
